import { useState } from "react";
import { Trash2 } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import type { ArchivedQuote } from "./ArchivePage";

// 刪除報價單的確認視窗(設計規格 §4、§9)。
//
// ── 打對單號,刪除鈕才亮 ────────────────────────────────────────
//
// 刪除是整個系統唯一沒有回頭路的動作:一叫下去,章節、明細與整份版本史都由
// cascade 一起帶走。所以確認不是「你確定嗎」那種按了就過的問句,而是要使用者
// **把單號原樣打一遍** —— 那一秒他一定會去看自己要刪的是哪一張。
//
// 這裡擋的是手滑,不是壞人:後端在 `DELETE /api/quotes/{id}` 一樣會比對單號,
// 只有前端擋的話,用 curl 就繞過去了(規格 §4)。兩邊的規矩必須一模一樣:
// **前後空白不算打錯**(從列表複製過來常常會帶到),但**分大小寫** ——
// 單號是識別碼,不是一句話,放寬大小寫等於少了一個字元的防呆。
//
// ── 輸入框的 state 掛在這個元件自己身上 ─────────────────────────
//
// 這是這一頁最容易做錯的一件事:確認用的輸入框如果掛在封存區那一頁的 state 上,
// 關掉視窗不會清掉它,於是上一張單打對的號碼會讓**下一張單的刪除鈕一打開就是
// 亮的** —— 整個防呆機制等於不存在。
//
// 所以 state 在這裡,而且呼叫端是「沒有目標就整個不渲染」+ `key={quote.id}`:
// 關掉就卸載、換一張單就換一個元件實例,打過的字自然留不住。改這裡之前請先看
// tests/archive-page.test.tsx 的「關掉刪除視窗再打開,打過的字不會留著」。
//
// 刪除失敗時**視窗不關**:錯誤訊息要留在使用者眼前,而他剛打的單號也還在,
// 不必再打一次。

export interface DeleteQuoteDialogProps {
  /** 要刪的那一張。**非空**:沒有目標時呼叫端不要渲染這個元件。 */
  quote: ArchivedQuote;
  /** 取消或關閉。 */
  onCancel: () => void;
  /** 真的刪掉了。呼叫端負責把清單重撈一次。 */
  onDeleted: () => void;
}

export function DeleteQuoteDialog({ quote, onCancel, onDeleted }: DeleteQuoteDialogProps) {
  const [typed, setTyped] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 與後端同一條規矩:前後空白不算打錯,但分大小寫。
  const matches = typed.trim() === quote.quote_no;

  const confirm = async () => {
    if (!matches || pending) return;
    setPending(true);
    setError(null);
    try {
      // body 帶單號 —— 後端會再比對一次,這一半才是真的防呆。
      await api.del(`/quotes/${quote.id}`, { quote_no: quote.quote_no });
      onDeleted();
    } catch (err) {
      // 失敗就停在原地:視窗不關,訊息用後端原句(單號不符、沒有權限、還沒封存)。
      setError(err instanceof ApiError ? err.message : "刪除失敗,請稍後再試");
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      title="刪除報價單"
      // 破壞性確認點背景不關:手滑關掉之後,使用者搞不清楚到底刪了沒。
      closeOnBackdrop={false}
      footer={
        <>
          <Button onClick={onCancel}>取消</Button>
          <Button
            variant="danger"
            icon={<Trash2 aria-hidden="true" size={16} />}
            disabled={!matches}
            loading={pending}
            onClick={confirm}
          >
            確定刪除
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="m-0 mb-3 text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-3">
        {/* 刪的是哪一張,單號與專案名稱都寫出來 —— 只寫單號的話,
            使用者沒辦法確認自己點到的是不是心裡想的那一張。 */}
        <p className="m-0 text-sm text-[var(--text)]">
          即將刪除 <span className="tabular font-medium">{quote.quote_no}</span>「{quote.title}」。
        </p>
        <p className="m-0 text-sm text-[var(--text-2)]">
          這個動作<strong className="font-semibold text-[var(--text)]">沒有回頭路</strong>
          :這張單的分類、明細與整份版本史都會一起消失,復原不回來。只有稽核紀錄會留著誰在什麼時候刪了哪一張。
        </p>
        <Input
          label="請輸入單號確認"
          hint={`請照原樣輸入 ${quote.quote_no}`}
          value={typed}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setTyped(event.target.value)}
          className="tabular"
        />
      </div>
    </Dialog>
  );
}
